/**
 * 현장 사진 API (모듈 20 — 검출).
 *
 * - 업로드: presigned PUT → SHA-256 (브라우저) → POST /site-photos
 * - 상태 폴링: detect_status 전이 pending → detecting → detected|failed
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './client'
import type { PresignedUploadResponse } from './bimFiles'

export type DetectStatus = 'pending' | 'detecting' | 'detected' | 'failed'

export interface SitePhoto {
  id: string
  project_id: string
  s3_key: string
  file_hash: string
  captured_at: string | null
  exif: Record<string, unknown>
  detect_status: DetectStatus
  current_detection_run_id: string | null
  created_at: string
}

export interface SitePhotoWithUrl extends SitePhoto {
  image_url: string
}

export interface DetectedObject {
  id: string
  photo_id: string
  detection_run_id: string | null
  class_name: string
  confidence: number
  bbox: { x1: number; y1: number; x2: number; y2: number }
  estimated_position: { x: number; y: number; z: number } | null
  estimated_dimensions: { w: number; h: number; d: number } | null
  created_at: string
}

export interface DetectionSource {
  run_id: string
  status: string
  execution_mode: string
  algorithm_version: string
  position_source: string
  official_eligible: boolean
  finished_at: string | null
}

export interface PhotoDetectionsResponse {
  photo: SitePhotoWithUrl
  detections: DetectedObject[]
  detection_source: DetectionSource | null
}

export function useSitePhotos(projectId: string | undefined, opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: ['site-photos', projectId],
    queryFn: async () =>
      (await api.get<SitePhoto[]>('/api/v1/site-photos', { params: { project_id: projectId } })).data,
    enabled: Boolean(projectId),
    refetchInterval: opts?.refetchInterval,
  })
}

export function usePhotoDetections(photoId: string | undefined) {
  return useQuery({
    queryKey: ['photo-detections', photoId],
    queryFn: async () =>
      (await api.get<PhotoDetectionsResponse>(`/api/v1/site-photos/${photoId}/detections`)).data,
    enabled: Boolean(photoId),
  })
}

export async function uploadSitePhoto(params: {
  projectId: string
  file: File
}): Promise<SitePhoto> {
  const { projectId, file } = params

  const presign = (
    await api.post<PresignedUploadResponse>('/api/v1/storage/presigned-upload', {
      project_id: projectId,
      filename: file.name,
      content_type: file.type || 'image/jpeg',
    })
  ).data

  const put = await fetch(presign.url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'image/jpeg' },
    body: file,
  })
  if (!put.ok) throw new Error(`S3 upload failed: ${put.status}`)

  const buf = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', buf)
  const hash = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

  return (
    await api.post<SitePhoto>('/api/v1/site-photos', {
      project_id: projectId,
      s3_key: presign.key,
      file_hash: hash,
      exif: {},
    })
  ).data
}

export function useUploadSitePhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: uploadSitePhoto,
    onSuccess: (p) => qc.invalidateQueries({ queryKey: ['site-photos', p.project_id] }),
  })
}

export function useRedetectPhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (photoId: string) =>
      (await api.post<SitePhoto>(`/api/v1/site-photos/${photoId}/redetect`)).data,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['site-photos', p.project_id] })
      qc.invalidateQueries({ queryKey: ['photo-detections', p.id] })
    },
  })
}
